/**
 * Rúbricas de calificación (`/api/escalas`).
 *
 * ⚠️ El nombre del archivo es LEGACY: la entidad se llama `Escala` desde 2026-07
 * (antes `CriterioEvaluacion`). El JSON `criterio` es polimórfico según
 * `tipo_informacion` — documental, cuantitativa y opinión tienen formas distintas
 * y no intercambiables.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type { CriterioEvaluacion, EscalaValoracion } from '@/interfaces/CriterioEvaluacion'
import { http } from '@/services/http'

const BASE = '/api/escalas'

/**
 * Consulta criterio evaluacion · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns CriterioEvaluacion[]
 */
export async function getCriteriosEvaluacionService(tipoInformacion?: string): Promise<CriterioEvaluacion[]> {
  const url = tipoInformacion ? `${BASE}?tipo_informacion=${tipoInformacion}` : BASE
  const result = await http.get<any>(url)
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : d?.data ?? []
}

/**
 * Escalas de valoración (tabla `grados`), que son OTRA cosa que las rúbricas de
 * `/api/escalas`: son las bandas de nota → nivel de cumplimiento. Ahí vive la
 * escala 1–10 de los modelos históricos, que por eso no se veía en esta
 * pantalla. Vienen agrupadas por modelo; la de `id_modelo: null` es la del
 * catálogo por defecto (MAAM 0–5).
 */
export async function getEscalasValoracionService(): Promise<EscalaValoracion[]> {
  const result = await http.get<any>('/api/escalas-valoracion')
  if (!result.ok) return []
  const d = result.data
  return Array.isArray(d) ? d : d?.data ?? []
}

/**
 * Consulta criterio evaluacion · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns CriterioEvaluacion | null
 */
export async function getCriterioEvaluacionByIdService(id: number): Promise<CriterioEvaluacion | null> {
  const result = await http.get<CriterioEvaluacion>(`${BASE}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Crea criterio evaluacion · petición `POST`.
 *
 * @returns { ok: boolean; data?: CriterioEvaluacion; errors?: Record<string, string[]> }
 */
export async function crearCriterioEvaluacionService(
  payload: Omit<CriterioEvaluacion, 'id'>
): Promise<{ ok: boolean; data?: CriterioEvaluacion; errors?: Record<string, string[]> }> {
  const result = await http.post<CriterioEvaluacion>(BASE, payload)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true, data: result.data }
}

/**
 * Actualiza criterio evaluacion · petición `PUT`.
 *
 * @returns { ok: boolean; data?: CriterioEvaluacion; errors?: Record<string, string[]> }
 */
export async function updateCriterioEvaluacionService(
  id: number,
  payload: Partial<CriterioEvaluacion>
): Promise<{ ok: boolean; data?: CriterioEvaluacion; errors?: Record<string, string[]> }> {
  const result = await http.put<any>(`${BASE}/${id}`, payload)
  if (!result.ok) return { ok: false, errors: result.errors }
  const data = result.data?.data ?? result.data
  return { ok: true, data }
}

/**
 * Elimina criterio evaluacion · petición `DELETE`.
 *
 * @returns { ok: boolean; errors?: Record<string, string[]> }
 */
export async function eliminarCriterioEvaluacionService(
  id: number
): Promise<{ ok: boolean; errors?: Record<string, string[]> }> {
  const result = await http.delete(`${BASE}/${id}`)
  if (!result.ok) return { ok: false, errors: result.errors }
  return { ok: true }
}
